import { Button, Group, Select, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { i18nKeys, tKeysLoose, useTranslation } from 'locales';
import {
  PAYMENT_PLAN_STEP_TYPES,
  RECOVERY_STEP_TYPES,
  type StepType,
  useDuplicateTemplate,
} from './api';

type DuplicateTemplateModalProps = {
  template: {
    id: string;
    name: string;
    stepType: string;
    planType: string;
  };
  onClose: () => void;
};

export const DuplicateTemplateModal = ({ template, onClose }: DuplicateTemplateModalProps) => {
  const { t } = useTranslation();
  const { duplicateTemplate, isDuplicateTemplateLoading } = useDuplicateTemplate();

  const stepTypes = template.planType === 'payment' ? PAYMENT_PLAN_STEP_TYPES : RECOVERY_STEP_TYPES;

  const form = useForm({
    initialValues: {
      name: `${template.name} (${t(i18nKeys.COPY)})`,
      stepType: template.stepType as StepType,
    },
    validate: {
      name: value => (value.trim().length === 0 ? t(i18nKeys.ERROR.FIELD_REQUIRED) : null),
    },
  });

  const handleSubmit = form.onSubmit(({ name, stepType }) => {
    duplicateTemplate({ id: template.id, name, stepType }, { onSuccess: onClose });
  });

  return (
    <form onSubmit={handleSubmit}>
      <Stack>
        <TextInput
          label={t(i18nKeys.NAME)}
          withAsterisk
          data-autofocus
          {...form.getInputProps('name')}
        />
        <Select
          label={t(i18nKeys.FORM.TYPE)}
          allowDeselect={false}
          data={stepTypes.map(stepType => ({
            value: stepType,
            label: t(tKeysLoose.TEMPLATE_TYPES[stepType.toUpperCase()]),
          }))}
          {...form.getInputProps('stepType')}
        />
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            {t(i18nKeys.CANCEL)}
          </Button>
          <Button type="submit" loading={isDuplicateTemplateLoading}>
            {t(i18nKeys.DUPLICATE)}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
